import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from '../lib/api'

/**
 * ONE membership-gated read, and the four club hooks are built on it.
 *
 * `useClubhouseBook`, `useRoundsFeed`, `useRegisterBook` and `useRoundCard` each
 * carried a hand-copied version of this state machine — four copies of the same
 * gate, the same refusal branch and the same `finally`. Two of the review's
 * findings were bugs that existed in some copies and not others (a loading flag
 * that never went true again; a generic fault that left the previous read on the
 * page), which is what a fourth copy buys you. They are fixed HERE, once.
 *
 * ## The gate is an ANSWER, not a fault
 *
 * Every one of these endpoints sits behind `Fireside::MembershipGate`, and a 403
 * `membership_required` is the club replying — so it clears the book, raises
 * `membershipRequired`, and raises NO error. The pages themselves stay ungated
 * on purpose: the standing is enforced on the data, never on the door.
 *
 * ## Nothing is held from a previous read — on ANY refusal
 *
 * A generic fault clears the data too, and that is a deliberate choice over the
 * alternative it replaced (keep the last good answer, print an alert above it).
 * Stale-behind-an-alert was rejected because the club's book has no timestamp on
 * the page: a member reading a register beside a red bar cannot tell whether the
 * rows are current, and a standing that lapsed between reads would leave the
 * whole book showing behind a message about a network. An empty book with a
 * stated fault is the honest shape; a full one that may be a session old is not.
 *
 * ## `isLoading` is DERIVED, and true for a REFETCH as well as the first read
 *
 * There is no `isLoading` state and no `finally` that sets it false. Loading is
 * "the current request has not answered yet", which is true on mount, true again
 * the moment the key changes, and true again the moment `refresh()` is called —
 * the flash the old copies had (`useState(enabled)`, captured at MOUNT, so a
 * session that resolved a beat later left the hook claiming it was settled while
 * the first fetch was still in flight, and the screen rendered its EMPTY plate)
 * cannot happen here. It also keeps state out of the effect body, which is what
 * `react-hooks/set-state-in-effect` is about.
 *
 * ## Every answer is STAMPED with the request it answers
 *
 * The idiom `useSharedRound` and `useCourseGeometry` already keep. A round read
 * for `/golf/rounds/r1` is not an answer about `r2`, so a page whose id changed
 * reads as LOADING rather than showing the previous round under the new id —
 * and a slow response that lands after the reader moved on is dropped rather
 * than painted. `key` is the request's identity: change it whenever the read's
 * target changes, or the book keeps answering the old question.
 */

/** What one request came back as — stamped with the request it answers. */
interface Answer<T> {
  key: string
  pass: number
  data: T | null
  membershipRequired: boolean
  refusal: string | null
  error: string | null
}

export interface GatedBook<T> {
  /**
   * null until the CURRENT request answers, and on every refusal.
   *
   * A hook that hands a screen a LIST coalesces this to one module-level empty
   * array rather than writing `?? []` — a fresh `[]` every render is a fresh
   * identity for every dependency array downstream.
   */
  data: T | null
  isLoading: boolean
  error: string | null
  membershipRequired: boolean
  /** The name `refusalOf` gave this refusal, or null. See below. */
  refusal: string | null
  refresh: () => Promise<void>
}

export interface GatedBookRead<T> {
  /**
   * The request's identity — the path, near enough. Every answer is stamped
   * with it and an answer to another key is never shown.
   */
  key: string
  /** The viewer holds a session. A visitor's console makes no request at all. */
  enabled: boolean
  /**
   * The read itself. Written as an inline closure at every call site, so it is
   * LATCHED rather than depended on — see the note on `latest` below.
   */
  read: () => Promise<T>
  /** What to say when the club faults in a way `Error` cannot name itself. */
  whenFaulty: string
  /**
   * Names a refusal this book answers with a state of its OWN rather than as an
   * error — `useRoundCard` maps a 404 to `notFound`, because the club
   * deliberately does not distinguish "no such round" from "not yours".
   *
   * It belongs here rather than in the caller precisely because the name has to
   * be stamped with the request like everything else: a 404 for the round the
   * reader just navigated away from must not plate "no such round" over the one
   * they are looking at.
   */
  refusalOf?: (error: unknown) => string | null
}

export function useGatedBook<T>({
  key,
  enabled,
  read,
  whenFaulty,
  refusalOf,
}: GatedBookRead<T>): GatedBook<T> {
  const [answer, setAnswer] = useState<Answer<T> | null>(null)
  // Bumped by `refresh` alone. It exists to force the render that makes
  // `isLoading` true again at the START of a refetch; the ref beside it is the
  // authority, so the effect can read the current pass without depending on it
  // and fetching twice.
  const [pass, setPass] = useState(0)
  const passRef = useRef(0)

  /**
   * The read and its classifier are LATCHED, not depended on. Both arrive as
   * inline closures, so a fresh identity every render would be a fresh request
   * every render — an endless fetch loop. `key` is what identifies a request,
   * and it is the only thing that reopens the book.
   */
  const latest = useRef({ read, refusalOf, whenFaulty })
  useEffect(() => {
    latest.current = { read, refusalOf, whenFaulty }
  })

  /** The request in flight — an answer to any other one is dropped unpainted. */
  const asked = useRef({ key, pass: 0 })

  const runPass = useCallback(
    (attempt: number): Promise<void> => {
      asked.current = { key, pass: attempt }
      const answered = (outcome: Omit<Answer<T>, 'key' | 'pass'>) => {
        if (asked.current.key !== key || asked.current.pass !== attempt) return
        setAnswer({ key, pass: attempt, ...outcome })
      }
      const { read: open, refusalOf: nameRefusal, whenFaulty: faulty } = latest.current

      return open()
        .then((data) => {
          answered({ data, membershipRequired: false, refusal: null, error: null })
        })
        .catch((e: unknown) => {
          // The one refusal that is an answer rather than a fault.
          if (e instanceof ApiError && e.code === 'membership_required') {
            answered({ data: null, membershipRequired: true, refusal: null, error: null })
            return
          }
          const named = nameRefusal?.(e) ?? null
          if (named !== null) {
            answered({ data: null, membershipRequired: false, refusal: named, error: null })
            return
          }
          answered({
            data: null,
            membershipRequired: false,
            refusal: null,
            error: e instanceof Error ? e.message : faulty,
          })
        })
    },
    [key],
  )

  const refresh = useCallback((): Promise<void> => {
    if (!enabled) return Promise.resolve()
    const attempt = passRef.current + 1
    passRef.current = attempt
    setPass(attempt)
    return runPass(attempt)
  }, [enabled, runPass])

  useEffect(() => {
    if (!enabled) return
    void runPass(passRef.current)
  }, [enabled, runPass])

  /**
   * DERIVED, never stored. An answer counts only for the request it was asked
   * about, so a changed key — or a refetch that has not landed — reads as
   * loading rather than as the previous answer.
   *
   * ⚠️ One boundary, stated rather than hidden: a gate that CLOSES and reopens
   * (`enabled` true → false → true on a signed-out-then-in reader who never
   * unmounted) re-reads, and the previous answer for the same key stays up for
   * the paint before the new one lands. The screens render their sign-in plate
   * in between, so nobody sees it; clearing on a flag that is also false during
   * every session bootstrap would blank the book on every mount instead.
   */
  const settled = answer !== null && answer.key === key && answer.pass === pass ? answer : null

  return {
    data: settled?.data ?? null,
    isLoading: enabled && settled === null,
    error: settled?.error ?? null,
    membershipRequired: settled?.membershipRequired ?? false,
    refusal: settled?.refusal ?? null,
    refresh,
  }
}
